import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { AlertTriangle, Fuel, Truck, Clock, RefreshCw, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function Anomalies() {
  const [data, setData] = useState({ count: 0, anomalies: [] });
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/anomalies");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const clear = async (jobId, type) => {
    if (!confirm("Confermi di aver gestito questa anomalia? Verrà rimossa dalla lista.")) return;
    setClearing(`${jobId}-${type}`);
    try {
      await api.post(`/jobs/${jobId}/anomalies/${type}/clear`);
      toast.success("Anomalia archiviata");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClearing(null);
    }
  };

  const km = data.anomalies.filter((a) => a.type === "km");
  const fuel = data.anomalies.filter((a) => a.type === "fuel");
  const time = data.anomalies.filter((a) => a.type === "time");

  return (
    <div className="space-y-8" data-testid="anomalies-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold mb-2">Controlli serali</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Anomalie Mezzi
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            Km fuori media e consumi carburante sospetti. Rivedi, verifica col caposquadra e archivia.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="font-mono font-bold text-amber-900" data-testid="anomalies-count">
              {data.count}
            </span>
            <span className="text-xs text-amber-700">aperte</span>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Aggiorna
          </Button>
        </div>
      </header>

      {data.anomalies.length === 0 && !loading && (
        <section className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-12 text-center" data-testid="no-anomalies">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
          <div className="font-display text-xl font-bold text-emerald-900">Tutto in ordine</div>
          <div className="text-sm text-emerald-700 mt-1">Nessuna anomalia rilevata sui mezzi.</div>
        </section>
      )}

      {time.length > 0 && (
        <section data-testid="time-anomalies-section">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-sky-600" /> Lavori più lunghi del solito <span className="text-sm font-mono text-slate-400">({time.length})</span>
          </h2>
          <ul className="space-y-3">
            {time.map((a) => (
              <li
                key={`${a.job_id}-time`}
                className={`bg-white border-l-4 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3 ${
                  a.severity === "critical" ? "border-rose-500" : "border-sky-500"
                }`}
                data-testid={`time-anomaly-${a.job_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-display font-bold text-slate-900">{a.client_name}</span>
                    <span className="text-[10px] uppercase tracking-widest font-bold bg-sky-100 text-sky-900 border border-sky-200 rounded-full px-2 py-0.5">
                      {a.details.job_type}
                    </span>
                    <span className="font-mono text-xs text-slate-400">{a.scheduled_date}</span>
                  </div>
                  <div className="text-sm text-slate-600 font-mono flex flex-wrap gap-x-4 gap-y-1">
                    <span>Durata effettiva: <b className="text-slate-900">{a.details.actual_hours.toFixed(1)}h</b></span>
                    <span>Media del tipo: <b className="text-slate-900">{a.details.median_hours.toFixed(1)}h</b> ({a.details.samples} lavori)</span>
                    <span className={`font-bold ${a.severity === "critical" ? "text-rose-700" : "text-sky-700"}`}>
                      +{a.details.deviation_pct.toFixed(1)}% sopra la media
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/lavori/${a.job_id}`}>
                    <Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Apri</Button>
                  </Link>
                  <Button
                    size="sm"
                    onClick={() => clear(a.job_id, "time")}
                    disabled={clearing === `${a.job_id}-time`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`clear-${a.job_id}-time`}
                  >
                    {clearing === `${a.job_id}-time` ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                    Archivia
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {km.length > 0 && (
        <section data-testid="km-anomalies-section">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Truck className="w-5 h-5 text-amber-600" /> Anomalie Km <span className="text-sm font-mono text-slate-400">({km.length})</span>
          </h2>
          <ul className="space-y-3">
            {km.map((a) => (
              <li
                key={`${a.job_id}-km`}
                className={`bg-white border-l-4 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3 ${
                  a.severity === "critical" ? "border-rose-500" : "border-amber-500"
                }`}
                data-testid={`km-anomaly-${a.job_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-display font-bold text-slate-900">{a.client_name}</span>
                    <span className="text-[10px] uppercase tracking-widest font-bold bg-amber-100 text-amber-900 border border-amber-200 rounded-full px-2 py-0.5">
                      {a.details.vehicle_plate}
                    </span>
                    <span className="font-mono text-xs text-slate-400">{a.scheduled_date}</span>
                  </div>
                  <div className="text-sm text-slate-600 font-mono flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      Km percorsi: <b className="text-slate-900">{Math.round(a.details.actual_km)}</b>
                    </span>
                    <span>
                      Media storica: <b className="text-slate-900">{Math.round(a.details.median_km)} km</b>
                    </span>
                    <span className={`font-bold ${a.severity === "critical" ? "text-rose-700" : "text-amber-700"}`}>
                      {a.details.deviation_pct >= 0 ? "+" : ""}
                      {a.details.deviation_pct.toFixed(1)}% {a.details.direction}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/lavori/${a.job_id}`}>
                    <Button variant="outline" size="sm" data-testid={`open-${a.job_id}`}>
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Apri
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    onClick={() => clear(a.job_id, "km")}
                    disabled={clearing === `${a.job_id}-km`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`clear-${a.job_id}-km`}
                  >
                    {clearing === `${a.job_id}-km` ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                    Archivia
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {fuel.length > 0 && (
        <section data-testid="fuel-anomalies-section">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Fuel className="w-5 h-5 text-rose-600" /> Anomalie Carburante <span className="text-sm font-mono text-slate-400">({fuel.length})</span>
          </h2>
          <ul className="space-y-3">
            {fuel.map((a) => (
              <li
                key={`${a.job_id}-fuel`}
                className="bg-white border-l-4 border-rose-500 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3"
                data-testid={`fuel-anomaly-${a.job_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-display font-bold text-slate-900">{a.client_name}</span>
                    <span className="text-[10px] uppercase tracking-widest font-bold bg-rose-100 text-rose-900 border border-rose-200 rounded-full px-2 py-0.5">
                      {a.details.vehicle_plate}
                    </span>
                    <span className="font-mono text-xs text-slate-400">{a.scheduled_date}</span>
                  </div>
                  <div className="text-sm text-slate-600 font-mono flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      Km: <b className="text-slate-900">{Math.round(a.details.km_driven || 0)}</b>
                    </span>
                    <span>
                      Previsti: <b>{(a.details.expected_liters || 0).toFixed(1)} L</b>
                    </span>
                    <span>
                      Effettivi: <b>{(a.details.actual_liters || 0).toFixed(1)} L</b>
                    </span>
                    <span className="font-bold text-rose-700">
                      Eccesso: +{(a.details.excess_liters || 0).toFixed(1)} L
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/lavori/${a.job_id}`}>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Apri
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    onClick={() => clear(a.job_id, "fuel")}
                    disabled={clearing === `${a.job_id}-fuel`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`clear-${a.job_id}-fuel`}
                  >
                    {clearing === `${a.job_id}-fuel` ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                    Archivia
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
